<script setup>
  import {computed, inject, nextTick, ref} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {useForm} from "@inertiajs/vue3";
  import ComponentRecordStatus from "@pages/Records/Components/RecordStatus.vue";
  import {MedButton, MedSpinner} from "@pharma4u/medicheck-components";
  import readDom from "@utils/Helpers/ReadDom.js";
  import {omit} from "lodash";

  defineOptions({
    name: "ComponentRecordCaseCloseDialog",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    record: {
      type: Object,
      required: true,
    },
    diseases: {
      type: Object,
      required: true,
    },
    laborvalues: {
      type: Object,
      required: true,
    },
    medications: {
      type: Object,
      required: true,
    },
    transfer: {
      type: Object,
      default: null,
    },
  });

  const privacy = inject("$privacy");
  const addToastMessage = inject("addToastMessage");

  const refDialog = ref(null);
  const processing = ref(false);
  const forceUpdateExistingDocuments = ref(false);

  const sumCommunicationDoctor = inject("sumCommunicationDoctor");
  const sumCommunicationPatient = inject("sumCommunicationPatient");

  const simulateResultConfig = inject("simulateResultConfig");
  const restoreResultConfig = inject("restoreResultConfig");

  const hasAnyPosDocument = computed(() => {
    return (
      props.transfer?.has_pos_documents?.doctor_pdf ||
      props.transfer?.has_pos_documents?.patient_pdf ||
      props.transfer?.has_pos_documents?.full_pdf
    );
  });

  const hasRelevantPosDocuments = computed(() => {
    if (forceUpdateExistingDocuments.value === true) {
      return false;
    }

    return (
      props.transfer?.has_pos_documents?.full_pdf === true ||
      props.transfer?.has_pos_documents?.doctor_pdf === true ||
      props.transfer?.has_pos_documents?.patient_pdf === true
    );
  });

  const form = useForm({
    doctor_html: null,
    patient_html: null,
    archive_html: null,
    bmp_data: {
      client: null,
      patient: null,
      diseases: null,
      laborvalues: null,
      medications: null,
    },
  });

  function open() {
    refDialog.value.open();

    // initialize bmp data as early as possible
    form.bmp_data.client = getClient();
    privacy.decryptPatient(props.patient).then((result) => {
      // do not send patient.note, patient.mp, patient.mp_date,
      // patient.contacts to the endpoint; these attributes
      // are not required
      form.bmp_data.patient = omit(result, ["contacts", "mp", "mp_date", "note"]);
    });

    form.bmp_data.diseases = props.diseases?.data ?? [];
    form.bmp_data.laborvalues = props.laborvalues?.data ?? [];
    form.bmp_data.medications = props.medications?.data ?? [];
  }

  function close() {
    // reset state; form.reset() does not work 😭
    form.doctor_html = null;
    form.patient_html = null;
    form.archive_html = null;
    form.bmp_data = {
      client: null,
      patient: null,
      diseases: null,
      laborvalues: null,
      medications: null,
    };
    form.wasSuccessful = false;
    processing.value = false;
    forceUpdateExistingDocuments.value = false;

    refDialog.value.close();
  }

  const storeDoctorPdf = computed(() => {
    return props.record.doctorsummary || sumCommunicationDoctor.value > 0;
  });

  const storePatientPdf = computed(() => {
    return props.record.patientsummary || sumCommunicationPatient.value > 0;
  });

  /**
   * @returns {Object} Client information
   */
  function getClient() {
    // in general, we should receive the client via the privacy vault
    let client = privacy.getClient();

    if (client?.company !== null || client?.first_name !== null || client?.last_name !== null) {
      return client;
    }

    // A@n attribute within BMP is mandatory; we use company or the
    // full name (first and last name) as a fallback. if both
    // are empty, we somehow have to provide a name,
    // otherwise the generated bmp is invalid!

    // check 1: is there a pharmacy object in related transfer data?
    if (props.transfer?.pharmacy !== null && typeof props.transfer?.pharmacy === "object") {
      // map data from transfer (PharmacyDto) to bmp_data.client
      client = {
        company: props.transfer?.pharmacy?.company_name ?? null,
        first_name: props.transfer?.pharmacy?.first_name ?? null,
        last_name: props.transfer?.pharmacy?.last_name ?? null,
        address: props.transfer?.pharmacy?.street_address ?? null,
        city: props.transfer?.pharmacy?.city ?? null,
        zip: props.transfer?.pharmacy?.postal_code ?? null,
        email: props.transfer?.pharmacy?.email ?? null,
        telephone: props.transfer?.pharmacy?.telephone ?? null,
      };

      if (client?.company !== null || client?.first_name !== null || client?.last_name !== null) {
        return client;
      }
    }

    // check 2: if still empty, use the record.username
    if (props.record?.username && typeof props.record?.username === "string") {
      return {
        company: null,
        first_name: null,
        last_name: props.record?.username,
        address: null,
        city: null,
        zip: null,
        email: null,
        telephone: null,
      };
    }

    // check 3: if still empty, we just provide a generic string
    // as the company name
    return {
      company: "Apotheke",
      first_name: null,
      last_name: null,
      address: null,
      city: null,
      zip: null,
      email: null,
      telephone: null,
    };
  }

  function save() {
    // handle processing indicator manually
    processing.value = true;

    // doctor
    if (storeDoctorPdf.value) {
      simulateResultConfig("showCommunicationDoc", true);
      simulateResultConfig("showCommunicationPatient", false);
      nextTick(() => {
        form.doctor_html = readDom("doc");
      });
    }

    // patient
    setTimeout(() => {
      if (storePatientPdf.value) {
        simulateResultConfig("showCommunicationDoc", false);
        simulateResultConfig("showCommunicationPatient", true);
        nextTick(() => {
          form.patient_html = readDom("patient");
        });
      }
    }, 200);

    // archive (always)
    setTimeout(() => {
      simulateResultConfig("showCommunicationDoc", false);
      simulateResultConfig("showCommunicationPatient", false);
      nextTick(() => {
        form.archive_html = readDom("archive");
      });
    }, 400);

    setTimeout(() => {
      form.post(route("records.store-to-transfer", {patient: props.record.patient_id, record: props.record.id}), {
        preserveScroll: true,
        only: ["flash", "errors", "record", "transfer"],
        onFinish: () => {
          processing.value = false;
          restoreResultConfig();
        },
        onError: (error) => {
          console.error(error);

          if (error.transfer) {
            addToastMessage(error.transfer, "error");
          }
        },
      });
    }, 600);
  }

  defineExpose({
    open,
    close,
  });
</script>

<template>
  <ComponentDialog
    ref="refDialog"
    :dialog-title="form.wasSuccessful || hasAnyPosDocument ? '' : 'Easy-com Kommunikation'"
    dialog-width="medium"
    :test-id="getTestId('case-close')"
  >
    <template #content>
      <template v-if="processing || form.processing">
        <h2 class="flex w-full justify-center space-x-4">
          <span class="text-xl">PDF-Dokumente werden übertragen...</span>
          <MedSpinner size="xl" :data-test="getTestId('case-close-dialog-spinner')" />
        </h2>

        <hr class="mt-4 h-px w-full bg-gray-500 p-0" />
      </template>

      <template v-else-if="!processing && form.wasSuccessful">
        <h2 class="flex w-full justify-center space-x-2 text-mcgreen">
          <component-icon class="mt-1 text-xl">check</component-icon>
          <span class="text-xl">Dokumente erfolgreich gespeichert</span>
        </h2>

        <hr class="mt-4 h-px w-full bg-gray-500 p-0" />

        <p class="mt-4">Die vorhandenen PDF-Dokumente wurden in der Warenwirtschaft gespeichert.</p>

        <p class="mt-4">
          Wenn Sie nachträglich Änderungen vornehmen möchten, ist ein erneutes Speichern der aktualisierten
          PDF-Dokumente in der Warenwirtschaft über „Dokumente gespeichert“ möglich.
        </p>

        <p class="mt-4">Möchten Sie den Status der Variante ebenfalls aktualisieren?</p>

        <h3 class="mt-2 font-bold">Status der Variante</h3>
        <ComponentRecordStatus :record="record" class="mt-2" inner-class="bottom-8 min-w-24 max-w-32" />
      </template>

      <template v-else-if="hasRelevantPosDocuments">
        <h2 class="flex w-full justify-center space-x-2 text-mcgreen">
          <ComponentIcon class="mt-1 text-xl">check</ComponentIcon>
          <span class="text-xl">Dokumente erfolgreich gespeichert</span>
        </h2>

        <p class="mt-2 text-center">Folgende PDF-Dokumente wurden bereits in der Warenwirtschaft gespeichert:</p>

        <ul class="mx-auto mt-2 w-32 list-disc">
          <li v-if="transfer?.has_pos_documents?.doctor_pdf">Arzt-PDF</li>
          <li v-if="transfer?.has_pos_documents?.patient_pdf">Patient-PDF</li>
          <li v-if="transfer?.has_pos_documents?.full_pdf">Archiv-PDF</li>
        </ul>

        <ComponentAlert
          v-if="!transfer?.has_pos_documents?.doctor_pdf && !transfer?.has_pos_documents?.patient_pdf"
          class="mt-4"
          :test-id="getTestId('case-close-dialog-alert-pdf-doc')"
        >
          <h4 class="font-bold">
            Bisher liegt nur das Gesamtdokument (Archiv-PDF) mit allen Ergebnissen vor. Dieses dient allein der internen
            Dokumentation!
          </h4>

          <p class="mt-2">
            Für die Kommunikation mit Arzt oder Patienten treffen Sie bitte
            <strong class="font-semibold">eine Auswahl</strong>
            der relevantesten Checkergebnisse mittels
            <strong class="font-semibold">„Kommunikation an Arzt“</strong>
            oder
            <strong class="font-semibold">„Kommunikation an Patienten“</strong>
            oder nutzen Sie das
            <strong class="font-semibold">Zusammenfassungsfeld.</strong>
          </p>
        </ComponentAlert>

        <hr class="mt-4 h-px w-full bg-gray-500 p-0" />

        <p class="mt-4">Möchten Sie ggf. geänderte PDF-Dokumente erneut speichern?</p>

        <p class="mt-4">
          Hinweis: Die bisher in der Warenwirtschaft gespeicherten PDF-Dokumente werden durch die aktuellen ersetzt.
        </p>

        <h3 class="mt-4 font-bold">Status der Variante</h3>
        <ComponentRecordStatus :record="record" class="mt-2" inner-class="bottom-8 min-w-24 max-w-32" />
      </template>

      <template v-else>
        <h2 class="text-base">Mit „Speichern“ werden folgende Dokumente in der Warenwirtschaft abgelegt:</h2>

        <ul class="mt-4 space-y-4">
          <li class="flex space-x-2">
            <div>
              <ComponentIcon v-if="storeDoctorPdf" class="text-2xl text-mcgreen-500">check</ComponentIcon>
              <ComponentIcon v-else class="text-2xl text-gray-500">close</ComponentIcon>
            </div>

            <div :class="{'text-gray-500': !storeDoctorPdf}">
              <h3 class="text-base font-bold">Arztkommunikation</h3>
              <div class="text-sm">
                Ein Arzt-PDF wird erzeugt, wenn Sie bei mindestens einem Checkergebnis die Auswahl „Info an Arzt“
                getroffen oder eine Zusammenfassung an den Arzt eingetragen haben.
              </div>
            </div>
          </li>

          <li class="flex space-x-2">
            <div>
              <ComponentIcon v-if="storePatientPdf" class="text-2xl text-mcgreen">check</ComponentIcon>
              <ComponentIcon v-else class="text-2xl text-gray-500">close</ComponentIcon>
            </div>

            <div :class="{'text-gray-500': !storePatientPdf}">
              <h3 class="text-base font-bold">Patientenkommunikation</h3>
              <div class="text-sm">
                Ein Patienten-PDF wird erzeugt, wenn Sie bei mindestens einem Checkergebnis die Auswahl „Info an
                Patienten“ getroffen oder eine Zusammenfassung an den Patienten eingetragen haben.
              </div>
            </div>
          </li>

          <li class="flex space-x-2">
            <div>
              <ComponentIcon class="text-2xl text-mcgreen">check</ComponentIcon>
            </div>

            <div>
              <h3 class="text-base font-bold">Alle Analyseergebnisse</h3>
              <div class="text-sm">
                Archiv-PDF mit allen Checkergebnissen für Ihre eigene Ablage; bitte nicht an Arzt / Patienten.
              </div>
            </div>
          </li>
        </ul>

        <ComponentAlert
          v-if="!storeDoctorPdf && !storePatientPdf"
          class="mt-4"
          type="warning"
          :test-id="getTestId('case-close-dialog-alert')"
        >
          <h4 class="font-bold">
            Bisher liegt nur das Gesamtdokument (Archiv-PDF) mit allen Ergebnissen vor. Dieses dient allein der internen
            Dokumentation!
          </h4>

          <p class="mt-2">
            Für die Kommunikation mit Arzt oder Patienten treffen Sie bitte
            <strong class="font-semibold">eine Auswahl</strong>
            der relevantesten Checkergebnisse mittels
            <strong class="font-semibold">„Kommunikation an Arzt“</strong>
            oder
            <strong class="font-semibold">„Kommunikation an Patienten“</strong>
            oder nutzen Sie das
            <strong class="font-semibold">Zusammenfassungsfeld.</strong>
          </p>
        </ComponentAlert>
      </template>
    </template>

    <template #actions>
      <template v-if="form.wasSuccessful">
        <MedButton variant="primary" :data-test="getTestId('case-close-dialog-close-button')" @click="close()">
          Schließen
        </MedButton>
      </template>

      <template v-else>
        <template v-if="hasRelevantPosDocuments">
          <MedButton
            variant="primary"
            :data-test="getTestId('case-close-dialog-save-again-button')"
            @click="forceUpdateExistingDocuments = true"
          >
            Erneut speichern
          </MedButton>
        </template>

        <template v-else>
          <MedButton
            variant="primary"
            :disabled="processing"
            :data-test="getTestId('case-close-dialog-save-button')"
            @click="save()"
          >
            Speichern
          </MedButton>
        </template>

        <MedButton :data-test="getTestId('case-close-dialog-close-button')" @click="close()">Abbrechen</MedButton>
      </template>
    </template>
  </ComponentDialog>
</template>
